import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Camera, Mic } from "lucide-react";

const EXPERIENCES = [
  {
    id: "event-fotografie",
    title: "Event Fotografie",
    subtitle: "Vastleggen wat je gasten voelen",
    text: "Van de eerste toost tot de laatste dans — sfeer, spontane blikken en tot leven gebrachte details.",
    href: "/events",
    icon: Camera,
    image: "https://images.pexels.com/photos/787961/pexels-photo-787961.jpeg?auto=compress&cs=tinysrgb&w=1600",
    from: "vanaf €275",
  },
  {
    id: "magic-mirror",
    title: "Magic Mirror Fotobooth",
    subtitle: "De ster van elk feest",
    text: "Een spiegel die praat, foto's maakt en direct print. Verrassend, uniek en fun voor jong & oud.",
    href: "/magic-mirror",
    icon: Sparkles,
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_capture-memories-11/artifacts/z4dhrm4m_da142cf9-af41-4e2e-be99-1fc6d98162c1.__CR0%2C0%2C1464%2C600_PT0_SX1464_V1___.jpg",
    from: "vanaf €395",
  },
  {
    id: "audio-guestbook",
    title: "Audio Gastenboek",
    subtitle: "De stemmen van jullie avond",
    text: "Een vintage telefoon waarin gasten een boodschap achterlaten. Iets wat foto's niet kunnen — hún stem.",
    href: "/audio-gastenboek",
    icon: Mic,
    image: "https://images.pexels.com/photos/1616113/pexels-photo-1616113.jpeg?auto=compress&cs=tinysrgb&w=1600",
    from: "vanaf €125",
  },
];

const Feestbeleving = () => {
  return (
    <section
      data-testid="feestbeleving-section"
      className="relative bg-ink text-white py-24 md:py-32 overflow-hidden"
    >
      {/* subtle bg accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-64 bg-gradient-to-b from-gold/10 to-transparent blur-3xl pointer-events-none" />

      <div className="container-x relative">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <p className="eyebrow text-gold mb-4">Feestbeleving</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Maak van je feest{" "}
            <span className="italic text-gold">nog meer herinneringen.</span>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed max-w-xl mx-auto">
            De emotie, het lachen, de dansvloer, de blikken tussendoor — dit zijn de momenten
            die jullie voor altijd willen terughoren en terugzien.
          </p>
        </div>

        {/* 3 experience tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {EXPERIENCES.map((exp, i) => {
            const Icon = exp.icon;
            return (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
              >
                <Link
                  to={exp.href}
                  data-testid={`feestbeleving-tile-${exp.id}`}
                  className="group relative block overflow-hidden aspect-[4/5] bg-neutral-900 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
                >
                  <img
                    src={exp.image}
                    alt={exp.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110 opacity-80 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />

                  {/* Icon badge */}
                  <div className="absolute top-6 left-6 w-11 h-11 rounded-full border border-gold/60 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <Icon size={16} className="text-gold" strokeWidth={1.4} />
                  </div>

                  {/* Price ribbon */}
                  <div className="absolute top-6 right-6 bg-gold text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                    {exp.from}
                  </div>

                  {/* Content */}
                  <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-end">
                    <span className="font-script text-gold text-2xl md:text-3xl leading-none mb-2">
                      {exp.subtitle}
                    </span>
                    <h3 className="font-serif text-white text-3xl md:text-4xl leading-tight">
                      {exp.title}
                    </h3>
                    <p className="text-sm text-white/70 mt-3 leading-relaxed max-w-xs group-hover:text-white/90 transition-colors">
                      {exp.text}
                    </p>
                    <div className="mt-5 flex items-center gap-3">
                      <span className="h-px w-8 bg-gold group-hover:w-14 transition-all duration-500" />
                      <span className="text-[10px] uppercase tracking-[0.3em] text-white/70 group-hover:text-gold transition-colors">
                        Ontdek &amp; Boek →
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Combi tip */}
        <div className="mt-14 flex flex-col md:flex-row items-center justify-center gap-4 text-center">
          <span className="text-xs text-white/40 uppercase tracking-[0.3em]">Tip</span>
          <span className="text-sm text-white/70">
            Combineer 2 of 3 belevingen en bespaar met onze combo's.
          </span>
        </div>
      </div>
    </section>
  );
};

export default Feestbeleving;
